import React, { useEffect, useState, useRef, Component } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Library, ShieldCheck, Eye, Activity, AlertTriangle, HeartPulse, Brain, ClipboardList, Stethoscope, FileText } from 'lucide-react';
interface Preview {
  src: string;
  label: string;
  caption: string;
  icon: ComponentType<any>;
}
const PREVIEWS: Preview[] = [{
  src: 'https://cdn.magicpatterns.com/uploads/6FRN1JRedjQvZLfBMCo3PS/IMG_1887.jpg',
  label: 'Estructura clínica',
  caption: 'Índice del Tomo 4',
  icon: Library
}, {
  src: 'https://cdn.magicpatterns.com/uploads/9TNea88Mz8FeMiC4Tgcmta/IMG_1880.jpg',
  label: 'Seguridad del paciente',
  caption: 'Errores frecuentes y cómo evitarlos',
  icon: ShieldCheck
}, {
  src: 'https://cdn.magicpatterns.com/uploads/ngkErqWzQVcyDmjnHo9f9A/IMG_1881.jpg',
  label: 'Primera impresión clínica',
  caption: 'Los primeros 30 segundos frente al paciente',
  icon: Eye
}, {
  src: 'https://cdn.magicpatterns.com/uploads/9g8CS82RFKhCsTN29ta1XA/IMG_1882.jpg',
  label: 'Signos vitales',
  caption: 'Por qué son tan importantes',
  icon: HeartPulse
}, {
  src: 'https://cdn.magicpatterns.com/uploads/pqyL3D4jkzGHYWyaeuWodc/IMG_1888.jpg',
  label: 'Señales de alarma',
  caption: 'Conciencia y signos vitales',
  icon: AlertTriangle
}, {
  src: 'https://cdn.magicpatterns.com/uploads/pixAJE29rPHvRq4hnEtFdc/IMG_1883.jpg',
  label: 'Valoración del dolor',
  caption: 'Qué preguntar sobre el dolor',
  icon: Activity
}, {
  src: 'https://cdn.magicpatterns.com/uploads/gV4ZY2US6bXRsPtCq2xJfB/IMG_1884.jpg',
  label: 'Escalas clínicas',
  caption: 'Escala de Glasgow paso a paso',
  icon: Brain
}, {
  src: 'https://cdn.magicpatterns.com/uploads/krKbrPC1y2aqNQd7DM5hds/IMG_1889.jpg',
  label: 'Protocolos clínicos',
  caption: 'Causas frecuentes de disnea',
  icon: ClipboardList
}, {
  src: 'https://cdn.magicpatterns.com/uploads/9fmhvpGjubAiD5kzMmtzdY/IMG_1890.jpg',
  label: 'Procedimientos hospitalarios',
  caption: 'Capítulo 9 — Caídas y síncope',
  icon: Stethoscope
}, {
  src: 'https://cdn.magicpatterns.com/uploads/ixuCTDdq5LB18AffC5GZcU/IMG_1891.jpg',
  label: 'Casos clínicos',
  caption: 'Ejemplo resuelto — plantilla 1',
  icon: FileText
}];
export function ContentPreview() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  // Drag-to-scroll state
  const isDown = useRef(false);
  const startX = useRef(0);
  const startScroll = useRef(0);
  const [isDragging, setIsDragging] = useState(false);
  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 8);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 8);
  };
  useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollState, {
      passive: true
    });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, []);
  const scrollBy = (dir: number) => {
    const el = scrollRef.current;
    if (!el) return;
    const cardW = el.querySelector('[data-preview-card]')?.clientWidth ?? 280;
    el.scrollBy({
      left: dir * (cardW + 20),
      behavior: 'smooth'
    });
  };
  // Desktop mouse drag
  const onMouseDown = (e: React.MouseEvent) => {
    const el = scrollRef.current;
    if (!el) return;
    isDown.current = true;
    startX.current = e.pageX - el.offsetLeft;
    startScroll.current = el.scrollLeft;
  };
  const onMouseMove = (e: React.MouseEvent) => {
    if (!isDown.current) return;
    const el = scrollRef.current;
    if (!el) return;
    e.preventDefault();
    const x = e.pageX - el.offsetLeft;
    const walk = (x - startX.current) * 1.2;
    if (Math.abs(walk) > 5) setIsDragging(true);
    el.scrollLeft = startScroll.current - walk;
  };
  const onMouseUp = () => {
    isDown.current = false;
    setTimeout(() => setIsDragging(false), 50);
  };
  return <section className="relative py-12 md:py-20 lg:py-24 bg-clinical-base overflow-hidden" data-id="element-46">
      {/* Soft clinical glow */}
      <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-[28rem] h-[28rem] bg-clinical-soft rounded-full blur-[140px] opacity-50 pointer-events-none" data-id="element-47"></div>
      <div className="absolute top-1/2 right-0 -translate-y-1/2 w-[24rem] h-[24rem] bg-clinical-softer rounded-full blur-[140px] opacity-60 pointer-events-none" data-id="element-48"></div>

      <div className="relative max-w-[1200px] mx-auto" data-id="element-49">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-8 md:mb-12 px-4 sm:px-6 lg:px-8" data-id="element-50">
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-[10px] sm:text-xs font-semibold tracking-[0.22em] uppercase text-clinical-mid mb-4" data-id="element-51">
            Parte del contenido
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-2xl sm:text-4xl md:text-5xl font-black text-clinical-deep mb-4 leading-[1.05] uppercase" data-id="element-52">
            Algo de lo que vas a encontrar{' '}
            <span className="accent-italic normal-case" data-id="element-53">adentro</span>
          </motion.h2>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-sm md:text-lg text-clinical-ink leading-relaxed" data-id="element-54">
            Algunas de las páginas reales del{' '}
            <span className="accent-italic" data-id="element-55">Mega Pack Enfermería</span>.
            Contenido organizado para prácticas hospitalarias, parciales y
            estudio clínico.
          </motion.p>
        </div>

        {/* Desktop scroll arrows */}
        <div className="hidden md:flex absolute top-1/2 right-6 lg:right-10 -translate-y-1/2 z-20 gap-2" data-id="element-56">
          <button type="button" onClick={() => scrollBy(-1)} disabled={!canScrollLeft} aria-label="Anterior" className={`w-11 h-11 rounded-full bg-white border border-clinical-line shadow-[0_8px_24px_rgba(11,42,74,0.12)] flex items-center justify-center transition-all ${canScrollLeft ? 'text-clinical-deep hover:bg-clinical-deep hover:text-white hover:border-clinical-deep' : 'text-clinical-ink/30 cursor-not-allowed opacity-60'}`} data-id="element-57">
            <ChevronLeft className="w-5 h-5" strokeWidth={2.5} data-id="element-58" />
          </button>
          <button type="button" onClick={() => scrollBy(1)} disabled={!canScrollRight} aria-label="Siguiente" className={`w-11 h-11 rounded-full bg-white border border-clinical-line shadow-[0_8px_24px_rgba(11,42,74,0.12)] flex items-center justify-center transition-all ${canScrollRight ? 'text-clinical-deep hover:bg-clinical-deep hover:text-white hover:border-clinical-deep' : 'text-clinical-ink/30 cursor-not-allowed opacity-60'}`} data-id="element-59">
            <ChevronRight className="w-5 h-5" strokeWidth={2.5} data-id="element-60" />
          </button>
        </div>

        {/* Scroll container with edge gradients */}
        <div className="relative" data-id="element-61">
          <div className="hidden md:block absolute top-0 left-0 bottom-4 w-12 bg-gradient-to-r from-clinical-base to-transparent z-10 pointer-events-none" data-id="element-62"></div>
          <div className="hidden md:block absolute top-0 right-0 bottom-4 w-12 bg-gradient-to-l from-clinical-base to-transparent z-10 pointer-events-none" data-id="element-63"></div>

          <div ref={scrollRef} onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp} className={`flex gap-4 md:gap-5 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-4 px-4 sm:px-6 lg:px-10 ${isDragging ? 'cursor-grabbing select-none' : 'md:cursor-grab'}`} style={{
          scrollPaddingLeft: '1rem',
          scrollPaddingRight: '1rem'
        }} data-id="element-64">
            {PREVIEWS.map((preview, idx) => <PreviewCard key={idx} preview={preview} idx={idx} data-id="element-65" />)}
            <div className="shrink-0 w-2 md:w-6" aria-hidden="true" data-id="element-66"></div>
          </div>
        </div>

        {/* Scroll hint mobile */}
        <div className="md:hidden flex items-center justify-center gap-1.5 mt-3 text-[11px] font-medium text-clinical-ink/50 px-4" data-id="element-67">
          <ChevronLeft className="w-3 h-3" strokeWidth={2.5} data-id="element-68" />
          Deslizá para ver más
          <ChevronRight className="w-3 h-3" strokeWidth={2.5} data-id="element-69" />
        </div>
      </div>
    </section>;
}
function PreviewCard({
  preview,
  idx
}: {
  preview: Preview;
  idx: number;
}) {
  const Icon = preview.icon;
  return <motion.div data-preview-card initial={{
    opacity: 0,
    y: 24
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-40px'
  }} transition={{
    delay: Math.min(idx * 0.06, 0.4),
    duration: 0.5,
    ease: 'easeOut'
  }} className="group shrink-0 snap-center w-[230px] sm:w-[260px] md:w-[300px]" data-id="element-70">
      {/* Frame */}
      <div className="relative aspect-[3/4] rounded-2xl overflow-hidden bg-white border border-clinical-line shadow-[0_12px_32px_rgba(11,42,74,0.08)] group-hover:shadow-[0_20px_48px_rgba(11,42,74,0.16)] group-hover:-translate-y-1 group-hover:border-clinical-mid/40 transition-all duration-300 will-change-transform" data-id="element-71">
        <div className="absolute inset-0 bg-gradient-to-br from-clinical-soft/0 to-clinical-soft/0 group-hover:from-clinical-soft/40 group-hover:to-transparent transition-opacity duration-500 pointer-events-none z-10" data-id="element-72"></div>

        <img src={preview.src} alt={preview.caption} draggable={false} className="absolute inset-0 w-full h-full object-cover object-top pointer-events-none" data-id="element-73" />

        <div className="absolute inset-0 ring-1 ring-inset ring-clinical-deep/5 rounded-2xl pointer-events-none" data-id="element-74"></div>
      </div>

      {/* Label below */}
      <div className="mt-4 px-1" data-id="element-75">
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-clinical-line text-[10px] font-bold tracking-[0.12em] uppercase text-clinical-mid mb-2" data-id="element-76">
          <Icon className="w-3 h-3" strokeWidth={2.5} data-id="element-77" />
          {preview.label}
        </div>
        <p className="text-sm md:text-[15px] font-semibold text-clinical-deep leading-snug" data-id="element-78">
          {preview.caption}
        </p>
      </div>
    </motion.div>;
}